import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  changePriorityStatus,
  changePriorityUrgency,
  createPriority,
  deletePriority,
  prioritiesListQuery,
  prioritiesShellQuery,
  setPriorityLabel,
  updatePriority,
  type PriorityRow,
} from '../../api/settingsEaseedeskPriorities'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import { PriorityMatrixPanel } from './PriorityMatrixPanel'

/**
 * Settings → Easeedesk → Ticket priorities.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * ticket_priorities/index.php` + `list.php`, si gest cu gest fata de
 * `public/js/views/easeedesk_settings/tickets/ticket_priorities/index-1.js` si
 * `list-1.js`: aceleasi opt coloane, aceleasi doua modale, aceleasi doua
 * comutatoare, aceeasi intrebare inainte de stergere.
 *
 * DE CE DOUA LISTE INTR-UN SINGUR TABEL
 * -------------------------------------
 *
 * `it_easeedesk_ticket_priority` n-are furnizor: prioritatile sunt randuri
 * COMUNE tuturor instantelor. Cele marcate `default` nu se pot redenumi - ar
 * schimba cuvantul pentru toata lumea - iar la CEC toate sunt default. De-aia
 * exista coloana "Shown as (this instance)": numele sub care le vede furnizorul
 * asta, scris in schema lui. Gol inseamna numele comun, si atunci casuta arata
 * numele comun ca sugestie.
 *
 * Sub ele stau prioritatile PROPRII (`default = 0`), singurele care au butoane
 * de editat si de sters. "Sters" inseamna scos din schema furnizorului; randul
 * comun ramane.
 */

/** Numele local se salveaza cand omul iese din casuta, ca in `list-1.js`. */
export function LocalLabel({ priority, onSaved }: { priority: PriorityRow; onSaved(): void }) {
  const [value, setValue] = useState(priority.label)
  const [saved, setSaved] = useState(priority.label)

  useEffect(() => {
    setValue(priority.label)
    setSaved(priority.label)
  }, [priority.label])

  const save = useMutation({
    mutationFn: (next: string) => setPriorityLabel(priority.ID, next),
    onSuccess: (_data, next) => {
      setSaved(next)
      toast(next ? `Shown as "${next}" here` : 'Back to the shared name', 'Priorities')
      onSaved()
    },
    onError: (error) => {
      /* N-a intrat: se pune la loc ce era, ca urmatoarea incercare sa nu para
         "neschimbat". Mesajul serverului conteaza - cand coloana inca nu
         exista, el spune de ce. */
      setValue(saved)
      toast(error instanceof ApiError && error.message ? error.message : 'The name could not be saved.', 'Priorities', 'error')
    },
  })

  return (
    <input
      type="text"
      className="form-control input-sm priority-label"
      data-priority={priority.ID}
      value={value}
      placeholder={priority.priority_name}
      disabled={save.isPending}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (value === saved) return
        save.mutate(value)
      }}
    />
  )
}

function PriorityRowCells({
  priority,
  onReload,
  onEdit,
}: {
  priority: PriorityRow
  onReload(): void
  onEdit(priority: PriorityRow): void
}) {
  const own = priority.default === 0

  const flip = useMutation({
    mutationFn: (v: { what: 'status' | 'urgency'; on: boolean }) =>
      v.what === 'status' ? changePriorityStatus(priority.ID, v.on) : changePriorityUrgency(priority.ID, v.on),
    onSuccess: () => {
      toast('Ticket type status changed', 'Success')
      onReload()
    },
    onError: () => {
      toast('Something went wrong.', 'Error', 'error')
      onReload()
    },
  })

  const remove = useMutation({
    mutationFn: () => deletePriority(priority.ID),
    onSuccess: () => {
      toast('Ticket priority deleted', 'Success')
      onReload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  return (
    <tr className="bg-white" id={`priority_${priority.ID}`}>
      <td>
        <span className="label" style={{ color: '#FFF', backgroundColor: priority.priority_color }}>
          {priority.priority_name}
        </span>
      </td>
      <td>{priority.priority_name}</td>
      <td>
        <LocalLabel priority={priority} onSaved={onReload} />
      </td>
      <td>
        <label className={own ? 'label label-primary' : 'label label-default'}>{own ? 'custom' : 'default'}</label>
      </td>
      <td>{priority.ID}</td>
      <td style={{ textAlign: 'center' }}>
        {own && (
          <div className="btn-group" role="group" aria-label="controls">
            <button
              type="button"
              className="btn btn-danger btn-xs delete-priority"
              data-priority={priority.scheme_id}
              onClick={() => {
                if (!window.confirm('Delete priority ?\nYou will not be able to recover this priority!')) return
                remove.mutate()
              }}
            >
              <i className="fas fa-trash-alt" aria-hidden="true"></i>
            </button>
            <button
              type="button"
              className="btn btn-primary btn-xs edit-priority"
              data-priority={priority.ID}
              onClick={() => onEdit(priority)}
            >
              <i className="fas fa-pencil-alt" aria-hidden="true"></i>
            </button>
          </div>
        )}
      </td>
      <td className="align-center" style={{ backgroundColor: '#e4b9c0' }}>
        <input
          type="checkbox"
          name="priority_urgency"
          aria-label={`High priority marker for ${priority.priority_name}`}
          title="High Priority Marker"
          checked={priority.urgency_flag === 1}
          onChange={(e) => flip.mutate({ what: 'urgency', on: e.target.checked })}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          name="priority_status"
          aria-label={`Status for ${priority.priority_name}`}
          title="Status"
          checked={priority.active === 1}
          onChange={(e) => flip.mutate({ what: 'status', on: e.target.checked })}
        />
      </td>
    </tr>
  )
}

export function TicketPrioritiesPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(prioritiesShellQuery())
  const list = useQuery(prioritiesListQuery())
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<PriorityRow | null>(null)

  /* `reloadList()` din index-1.js: dupa fiecare scriere lista se cere din nou. */
  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'ticket_priorities'] })
  }

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="Ticket priorities" />
  if (!shell.data) return <Spinner />

  const rows = [...(list.data?.default_priorities ?? []), ...(list.data?.custom_priorities ?? [])]

  return (
    <div>
      <h4>
        <strong>Ticket</strong> priorities
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit ticket <strong>priorities</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create priority
      </button>
      <hr />

      <div className="list-group">
        {list.error ? (
          <LoadFailed error={list.error} onRetry={() => void list.refetch()} what="The priority list" />
        ) : !list.data ? (
          <Spinner />
        ) : (
          <table className="table table-hover dataTable">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Display name</th>
                <th style={{ textAlign: 'left' }}>Priority name</th>
                <th style={{ textAlign: 'left' }} width={180}>
                  Shown as (this instance)
                </th>
                <th style={{ textAlign: 'left' }}>Type</th>
                <th style={{ textAlign: 'left' }}>Level</th>
                <th>Actions</th>
                <th width={120}>High Priority Marker</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((priority) => (
                <PriorityRowCells key={priority.ID} priority={priority} onReload={reload} onEdit={setEdited} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Matricea Urgenta x Impact (CEC L25): alege una din prioritatile de mai sus. */}
      <PriorityMatrixPanel />

      <PriorityModal open={creating} onClose={() => setCreating(false)} onSaved={reload} />
      <PriorityModal priority={edited ?? undefined} open={edited !== null} onClose={() => setEdited(null)} onSaved={reload} />
    </div>
  )
}

/**
 * `newPriorityModal` si `editPriorityModal` din macheta: acelasi formular, cu
 * doua campuri. Culoarea se alege cu selectorul browserului in locul lui
 * colpick - aceeasi valoare (`#rrggbb`), fara o biblioteca de jQuery in plus.
 */
export function PriorityModal({
  priority,
  open,
  onClose,
  onSaved,
}: {
  priority?: PriorityRow
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const editing = priority !== undefined
  const [name, setName] = useState('')
  const [color, setColor] = useState('#0000ff')

  useEffect(() => {
    if (!open) return
    setName(priority?.priority_name ?? '')
    setColor(priority?.priority_color !== undefined && priority.priority_color !== '' ? priority.priority_color : '#0000ff')
  }, [open, priority])

  const save = useMutation({
    mutationFn: () =>
      editing ? updatePriority(priority.ID, name.trim(), color) : createPriority(name.trim(), color),
    onSuccess: () => {
      toast(editing ? 'Priority successfully updated.' : 'New priority successfully created.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={editing ? 'editPriorityModal' : 'newPriorityModal'}
      labelledBy={editing ? 'editPriorityModalLabel' : 'newPriorityModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit priority' : 'New ticket priority'}
    >
      <form
        id={editing ? 'edit-priority-form' : 'new-priority-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label htmlFor={editing ? 'edit-priority-name' : 'priority-name'} className="control-label">
            Priority name:
          </label>
          <input
            type="text"
            className="form-control"
            id={editing ? 'edit-priority-name' : 'priority-name'}
            name="priority_name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-group">
          <label htmlFor={editing ? 'edit-priority-color' : 'priority-color'} className="control-label">
            Priority color:
          </label>
          <input
            type="color"
            className="form-control"
            id={editing ? 'edit-priority-color' : 'priority-color'}
            name="priority_color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
          />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className={editing ? 'btn btn-primary submit-update-priority' : 'btn btn-primary submit-new-priority'}
            disabled={name.trim() === '' || save.isPending}
          >
            {editing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
